import React, { useRef, useState, useMemo, useEffect } from 'react'
import { Card, CardContent, makeStyles, AppBar, Tabs, Tab, Divider } from '@material-ui/core'
import FullCalendar from '@fullcalendar/react'
import interactionPlugin from '@fullcalendar/interaction'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import { addMinutes } from 'date-fns'
import calendarPageStyles from './calendarPage.styles'
import { useTeamCalendarContext } from '../../../context/useTeamCalendarContext'
import CreateEventDrawer from '../CreateEventDrawer'
import CalendarLiveStreams from '../CalendarLiveStreams/CalendarLiveStreams'
import CalendarEventClickModal from '../CalendarEventClickModal/CalendarEventClickModal'
import setFakeEvent from '../../../utils/helpers/eventHelpers/setFakeEvents'
import LoaderCircle from '../../../components/loaders/LoaderCircle/LoaderCircle'
import useHistoryPush from '../../../hooks/useHistoryPush'
import useTour from '../../../hooks/useTour'
import calendarEventModalDimensions from '../../../utils/constants/calendarEventModalDimensions'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import TabPanel from '../../../components/TabPanel/TabPanel'
import COLORS from '../../../utils/constants/colors'
import Standings from '../Standings/Standings'
import StageFilter from '../../CmsAdminPage/StageFilter/StageFilter'
import GroupFilter from '../../CmsAdminPage/GroupFilter/GroupFilter'
import CompetitionFilter from '../../CmsAdminPage/CompetitionFilter/CompetitionFilter'
import localStorageIds from '../../../utils/constants/localStorageIds'
import { setLocalStorageValue, removeLocalStorageValue } from '../../../utils/helpers/localStorageHelperMethods'
import { useQuery } from 'react-query'
import QueryKeys from '../../../utils/constants/queryKeys'
import ORGANISATION_TYPES from '../../../utils/constants/OrganisationTypes'
import getStages from '../../../Apis/stages/getStages'
import getFederationStructure from '../../../Apis/competition/getFederationStructure'
import SeasonFilter from '../../CmsAdminPage/SeasonFilter/SeasonFilter'
import USER_PERMISSIONS from '../../../utils/constants/userPermissions'
import hasPermission from '../../../utils/helpers/hasPermission'
import useIsMobileScreen from '../../../hooks/useIsMobileScreen'
import MobileSchedule from '../MobileSchedule/MobileSchedule'
import usePairedCalendarGames from '../../../hooks/usePairedCalendarGames'
import useQueryParams from '../../../hooks/useQueryParams'
import isOrgAddonsValid from '../../../utils/helpers/isOrgAddonsValid'
import { USER_ADDONS } from '../../../utils/constants/userAddons'

const CalendarPage = () => {
  const {
    setClickedEventId,
    setClickedEventOrgId,
    setCoordinatesModal,
    currentTeam,
    teams,
    events,
    setDates,
    setCreateEventDrawerOpen,
    setDateClick,
    setEvents,
    eventsRaw,
    selectedEventType,
    eventLoading,
    eventTypes,
  } = useTeamCalendarContext()
  const [eventClickModalOpen, setEventClickModalOpen] = useState(false)
  // The federation behind the clicked event when it is a paired game, read-only.
  const [clickedPaired, setClickedPaired] = useState(null)
  const [visibleRange, setVisibleRange] = useState(null)
  const [viewType, setViewType] = useState('dayGridMonth')
  const [tab, setTab] = useState(0)
  const [selectedStage, setSelectedStage] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const introTourName = 'intro-tour'
  const localStorageValue = useGetLocalStorage()
  const storedCompetition = localStorageValue?.[localStorageIds.SELECTED_COMPETITION] || null
  const storedSeason = localStorageValue?.[localStorageIds.SELECTED_SEASON] || null
  const storedStage = localStorageValue?.[localStorageIds.SELECTED_STAGE] || null
  const storedGroup = localStorageValue?.[localStorageIds.SELECTED_GROUP] || null
  const [selectedCompetition, setSelectedCompetition] = useState(null)
  const [selectedFederationSeason, setSelectedFederationSeason] = useState(null)
  const { [introTourName]: calendarTourValue } = localStorageValue || {}
  const selectedOrgId = localStorageValue?.selectedOrg?.id
  const { [localStorageIds.SELECTED_SEASON]: selectedSeason } = localStorageValue || {}
  const { id: seasonId } = selectedSeason || {}
  const orgId = localStorageValue?.selectedOrg?.id
  const selectedOrg = localStorageValue?.selectedOrg
  const isFederation = selectedOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION
  // Members without CMS access (e.g. Video operators, who only see their assigned games) get the
  // plain calendar: the filters and standings read CMS endpoints the server refuses them.
  const canReadCms = hasPermission(USER_PERMISSIONS.READ_CMS)
  const showTabs = canReadCms && (selectedOrg?.type === ORGANISATION_TYPES.ORG_COMPETITION ||
                   selectedOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION)
  const { redirect, pathname } = useHistoryPush()
  // A phone gets the calendar read-only, whatever the user's role would allow on a desktop.
  const isMobileScreen = useIsMobileScreen()
  const useStyles = makeStyles(calendarPageStyles)
  const classes = useStyles()

  // A club holding ViewStats sees its team's paired federation games next to its own events.
  const { team: teamId } = useQueryParams()
  const pairedEvents = usePairedCalendarGames({
    orgId,
    teamId,
    range: visibleRange,
    color: eventTypes?.find((t) => t.code === 'game')?.color || COLORS.purple,
    enabled: selectedOrg?.type === ORGANISATION_TYPES.ORG_CLUB && isOrgAddonsValid([USER_ADDONS.VIEW_STATS]),
  })
  const calendarEvents = useMemo(
    () => (pairedEvents.length ? [...(events || []), ...pairedEvents] : events),
    [events, pairedEvents],
  )
  const onDatesSet = (dateInfo) => {
    setDates(dateInfo)
    setVisibleRange({ start: dateInfo.start, end: dateInfo.end })
  }
  const openEvent = (eventId, paired) => {
    setClickedPaired(paired || null)
    setClickedEventOrgId(paired?.orgId || null)
    setClickedEventId(eventId)
  }
  const introRef = useRef()
  const tipRef = useRef()

  const tourToShowName = calendarTourValue ? 'hint-tour' : 'intro-tour'
  const tourToShowSteps = calendarTourValue ? 'hint' : 'calendar'
  const refName = calendarTourValue ? introRef : tipRef
  // Change this value to trigger the tip tour to show again (Always string)
  // Change the message from "createSteps" file
  const tipReshowKey = '3'

  useTour({
    elRef: refName,
    tourName: tourToShowName,
    tourSteps: tourToShowSteps,
    tourDesc: calendarTourValue ? tipReshowKey : null,
  })

  const { calendarEventModalWidth, calendarEventModalHeight } = calendarEventModalDimensions

  const onEventClick = (eventInfo) => {
    // TODO: Probably can transfer the position logic in the modal component to calculate the dimensions and position dynamically and not set them in constant and state.
    // (document.getElementById('EventModal')?.offsetWidth)
    const bodyRect = document.body.getBoundingClientRect()
    const elemRect = eventInfo.el.getBoundingClientRect()
    const middleOfElementLeft = (elemRect.left + elemRect.right) / 2
    let offsetLeft
    let offsetTop

    const SHOW_LEFT = 'showLeft'
    const SHOW_RIGHT = 'showRight'
    const SHOW_MIDDLE = 'showMiddle'

    const SHOW_CASES = {
      [SHOW_LEFT]: () => {
        offsetLeft = elemRect.left - bodyRect.left - calendarEventModalWidth
      },
      [SHOW_RIGHT]: () => {
        offsetLeft = elemRect.left - bodyRect.left
      },
      [SHOW_MIDDLE]: () => {
        offsetLeft = null
      },
    }

    if (middleOfElementLeft > calendarEventModalWidth) {
      SHOW_CASES[SHOW_LEFT]()
    } else if (bodyRect.width - middleOfElementLeft > calendarEventModalWidth) {
      SHOW_CASES[SHOW_RIGHT]()
    } else {
      SHOW_CASES[SHOW_MIDDLE]()
    }

    if (bodyRect.height - elemRect.top < calendarEventModalHeight) {
      offsetTop = elemRect.top - bodyRect.top - calendarEventModalHeight
    } else {
      offsetTop = elemRect.top - bodyRect.top
    }
    setEventClickModalOpen(true)
    setCoordinatesModal({ x: offsetLeft, y: offsetTop })
    openEvent(eventInfo.event['_def'].publicId, eventInfo.event.extendedProps?.paired)
  }

  // Held in one place because the list view's New Event button has to answer the same question
  // before it renders: a button that silently does nothing is worse than no button.
  const canCreateEvent = useMemo(
    () =>
      !isMobileScreen &&
      Boolean(eventTypes?.length) &&
      !showTabs &&
      hasPermission(USER_PERMISSIONS.CREATE_EVENT),
    [isMobileScreen, eventTypes, showTabs, currentTeam, teams],
  )

  // The list view has no empty day cell to click, so it gets a toolbar button instead. It opens
  // on today, since no day is under the cursor. The calendar reports its own view rather than the
  // context's date range, which the provider keeps to itself.
  const showNewEventButton = canCreateEvent && viewType === 'listMonth'

  const onDateClick = (info) => {
    if (!canCreateEvent) return

    const now = new Date()
    let start = new Date(info.date)
    start.setHours(now.getHours())
    start.setMinutes(now.getMinutes())
    start = addMinutes(start, 30)
    const end = addMinutes(start, 30)

    setDateClick({ start, end })
    setCreateEventDrawerOpen(true)
    setFakeEvent(setEvents, eventsRaw, selectedEventType, start, end)
    redirect(pathname, [{ breadcrumb: 'create' }])
  }

  const handleTabChange = (event, newValue) => {
    setTab(newValue)
  }

  const a11yProps = (index) => {
    return {
      id: `calendar-tab-${index}`,
      'aria-controls': `calendar-tabpanel-${index}`,
    }
  }

  const handleStageChange = (stage) => {
    setSelectedStage(stage)
    setSelectedGroup(null)
    if (stage) {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_STAGE, value: stage }])
      removeLocalStorageValue([localStorageIds.SELECTED_GROUP])
    } else {
      removeLocalStorageValue([localStorageIds.SELECTED_STAGE, localStorageIds.SELECTED_GROUP])
    }
  }

  const handleGroupChange = (group) => {
    setSelectedGroup(group)
    if (group) {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_GROUP, value: group }])
    } else {
      removeLocalStorageValue([localStorageIds.SELECTED_GROUP])
    }
  }

  const handleCompetitionChange = (competition) => {
    setSelectedCompetition(competition)
    if (competition) {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_COMPETITION, value: competition }])
    } else {
      removeLocalStorageValue([localStorageIds.SELECTED_COMPETITION])
    }
    setSelectedStage(null)
    setSelectedGroup(null)
    removeLocalStorageValue([localStorageIds.SELECTED_STAGE, localStorageIds.SELECTED_GROUP])

    // Preselect first season if available
    const seasons = federationStructure
      ?.find((c) => c.id === competition?.id)
      ?.seasons
    const season = seasons?.[0] || null
    setSelectedFederationSeason(season)
    if (season) {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_SEASON, value: season }])
    } else {
      removeLocalStorageValue([localStorageIds.SELECTED_SEASON])
    }
  }

  const handleFederationSeasonChange = (season) => {
    setSelectedFederationSeason(season)
    if (season) {
      setLocalStorageValue([{ id: localStorageIds.SELECTED_SEASON, value: season }])
    } else {
      removeLocalStorageValue([localStorageIds.SELECTED_SEASON])
    }
    setSelectedStage(null)
    setSelectedGroup(null)
    removeLocalStorageValue([localStorageIds.SELECTED_STAGE, localStorageIds.SELECTED_GROUP])
  }

  // Federation: fetch the full competition→season→stage→group tree
  const { data: federationStructure } = useQuery(
    [QueryKeys.GET_FEDERATION_STRUCTURE, orgId],
    () => getFederationStructure(orgId),
    { enabled: isFederation && canReadCms && !!orgId },
  )

  // Competition: fetch stages directly using localStorage season
  const { data: stages } = useQuery(
    [QueryKeys.GET_STAGES, orgId, seasonId],
    () => getStages({ orgId, seasonId }),
    { enabled: !isFederation && showTabs && !!orgId && !!seasonId },
  )

  // Restore competition and season from localStorage when federation structure loads
  useEffect(() => {
    if (!federationStructure || !storedCompetition || selectedCompetition) return

    const comp = federationStructure.find((c) => c.id === storedCompetition.id)
    if (!comp) return

    setSelectedCompetition(comp)

    // Restore season within that competition
    let restoredSeason = null
    if (storedSeason) {
      restoredSeason = comp.seasons?.find((s) => s.id === storedSeason.id)
    }
    if (!restoredSeason && comp.seasons?.length > 0) {
      restoredSeason = comp.seasons[0]
    }
    if (restoredSeason) {
      setSelectedFederationSeason(restoredSeason)
    }
  }, [federationStructure, storedCompetition])

  // For federation: derive stages from the selected season within the structure
  const federationStages = selectedFederationSeason
    ? federationStructure
        ?.find((c) => c.id === selectedCompetition?.id)
        ?.seasons?.find((s) => s.id === selectedFederationSeason?.id)
        ?.stages ?? []
    : undefined

  // Restore stage and group from localStorage
  const activeStages = isFederation ? federationStages : stages
  useEffect(() => {
    if (!activeStages || selectedStage) return

    if (storedStage) {
      const stage = activeStages.find((s) => s.id === storedStage.id)
      if (stage) {
        setSelectedStage(stage)

        if (storedGroup) {
          const group = stage.groups?.find((g) => g.id === storedGroup.id)
          if (group) {
            setSelectedGroup(group)
          }
        }
      }
    }
  }, [activeStages, storedStage, storedGroup])

  // Reset when organization changes
  useEffect(() => {
    setTab(0)
    setSelectedCompetition(null)
    setSelectedFederationSeason(null)
    setSelectedStage(null)
    setSelectedGroup(null)
  }, [selectedOrgId])

  // The tab panel's 24px padding sits inside the page's own; on a phone the calendar needs that room.
  const tabPanelStyle = isMobileScreen ? { padding: '0 12px' } : { paddingTop: 0 }

  const tabs = useMemo(() => {
    if (showTabs) {
      return [
        { label: 'Calendar' },
        { label: 'Standings' },
      ]
    }
    return []
  }, [showTabs])

  // Google Calendar's schedule instead of FullCalendar: its month grid and list are too dense at
  // phone width. Same events, same date-range query, same read-only details popup.
  const mobileSchedule = (
    <>
      <MobileSchedule
        events={calendarEvents}
        loading={eventLoading}
        onMonthChange={onDatesSet}
        onEventClick={(eventId) => {
          setCoordinatesModal({ x: 0, y: 0 })
          openEvent(eventId, pairedEvents.find((e) => e.id === eventId)?.paired)
          setEventClickModalOpen(true)
        }}
      />
      <CalendarEventClickModal
        open={eventClickModalOpen}
        handleClose={() => setEventClickModalOpen(false)}
        readOnly
        paired={clickedPaired || undefined}
      />
    </>
  )

  return (
    <div ref={introRef}>
      {/* Filters Section - Visible for competition/federation orgs */}
      <ConditionalRender renderValue={showTabs}>
        <div className={classes.filtersContainer}>
          {/* Federation: competition + season selectors */}
          {isFederation && (
            <>
              <CompetitionFilter
                competitions={federationStructure}
                selectedCompetition={selectedCompetition}
                onCompetitionChange={handleCompetitionChange}
              />

              {selectedCompetition && (
                <SeasonFilter
                  seasons={
                    federationStructure
                      ?.find((c) => c.id === selectedCompetition.id)
                      ?.seasons ?? []
                  }
                  selectedSeason={selectedFederationSeason}
                  onSeasonChange={handleFederationSeasonChange}
                />
              )}
            </>
          )}

          {/* Stage + Group filters */}
          {(!isFederation || selectedFederationSeason) && (
            <>
              <StageFilter
                stages={isFederation ? federationStages : stages}
                selectedStage={selectedStage}
                onStageChange={handleStageChange}
              />
              {selectedStage && (
                <GroupFilter
                  selectedStage={selectedStage}
                  selectedGroup={selectedGroup}
                  onGroupChange={handleGroupChange}
                />
              )}
            </>
          )}
        </div>
      </ConditionalRender>

      {showTabs ? (
        <Card className={classes.card}>
          <AppBar position="static" color="transparent" elevation={0}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { background: COLORS.orange } }}
              textColor="inherit"
            >
              {tabs.map((tabItem, index) => (
                <Tab
                  key={index}
                  label={tabItem.label}
                  {...a11yProps(index)}
                  style={{
                    color: tab === index ? COLORS.orange : COLORS.grey,
                  }}
                />
              ))}
            </Tabs>
          </AppBar>

          <Divider light orientation="horizontal" />

          <TabPanel value={tab} index={0} style={tabPanelStyle}>
            <ConditionalRender renderValue={!isMobileScreen}>
              <CalendarLiveStreams />
              <CreateEventDrawer />
            </ConditionalRender>
            {isMobileScreen ? (
              mobileSchedule
            ) : (
              <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                  <FullCalendar
                    initialView="dayGridMonth"
                    plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                    datesSet={(dateInfo) => setDates(dateInfo)}
                    events={events}
                    eventClick={onEventClick}
                    dateClick={onDateClick}
                    dayMaxEvents
                    weekNumberCalculation="ISO"
                    customButtons={{
                      customLoader: {
                        text: <LoaderCircle loading={eventLoading} />,
                      },
                    }}
                    headerToolbar={{
                      start: 'prev,next,today,customLoader',
                      center: 'title',
                      end: 'dayGridMonth,dayGridWeek,dayGridDay,listMonth',
                    }}
                    buttonText={{
                      today: 'TODAY',
                      month: 'MONTH',
                      week: 'WEEK',
                      day: 'DAY',
                      list: 'LIST',
                    }}
                    slotLabelFormat={{
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    }}
                    eventTimeFormat={{
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    }}
                  />
                  <CalendarEventClickModal
                    open={eventClickModalOpen}
                    handleClose={() => setEventClickModalOpen(false)}
                  />
                </CardContent>
              </Card>
            )}
          </TabPanel>
          <TabPanel value={tab} index={1} style={tabPanelStyle}>
            <Standings />
          </TabPanel>
        </Card>
      ) : (
        <>
          <ConditionalRender renderValue={!isMobileScreen}>
            <CalendarLiveStreams />
            <CreateEventDrawer />
          </ConditionalRender>
          {isMobileScreen ? (
            mobileSchedule
          ) : (
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <FullCalendar
                  initialView="dayGridMonth"
                  plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                  datesSet={(dateInfo) => {
                    onDatesSet(dateInfo)
                    setViewType(dateInfo.view.type)
                  }}
                  events={calendarEvents}
                  eventClick={onEventClick}
                  dateClick={onDateClick}
                  dayMaxEvents
                  weekNumberCalculation="ISO"
                  customButtons={{
                    customLoader: {
                      text: <LoaderCircle loading={eventLoading} />,
                    },
                    newEvent: {
                      text: '+ NEW EVENT',
                      click: () => onDateClick({ date: new Date() }),
                    },
                  }}
                  headerToolbar={{
                    start: showNewEventButton
                      ? 'prev,next,today,newEvent,customLoader'
                      : 'prev,next,today,customLoader',
                    center: 'title',
                    end: 'dayGridMonth,dayGridWeek,dayGridDay,listMonth',
                  }}
                  buttonText={{
                    today: 'TODAY',
                    month: 'MONTH',
                    week: 'WEEK',
                    day: 'DAY',
                    list: 'LIST',
                  }}
                  slotLabelFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  }}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  }}
                />
                <CalendarEventClickModal
                  open={eventClickModalOpen}
                  handleClose={() => setEventClickModalOpen(false)}
                  paired={clickedPaired || undefined}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default CalendarPage
